import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { userMeetsPlan } from "./PlanGate";
import "./TaskForm.css";
import "./task/taskform.css";
import { Modal, Button, TitleInput, RichDropdown, SegmentedControl, ChipGroup, Toggle, Checkbox, Input, Textarea, Select, DateInput, TimeInput, Banner, Avatar } from "../ui";
import { taskTypeOptions } from "./task/taskTypeStyle";
import AssignPicker from "./task/AssignPicker";
import { formatTaskDay, formatTaskClock } from "../utils/taskTime";
import { api } from "../api/client";
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast } from '../utils/toast';        // Block 233
import { uploadStoredFile, openStoredFile, fileTooBig } from "../utils/storedFiles";
import {
  createTeamsMeeting
} from "../utils/teamsUtils";
import { useWorkspaceRoster, rosterMentionSuggestions, rosterParseMentions } from "../hooks/useWorkspaceRoster";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import LocationInput from "./LocationInput";
import { getTravelInfo, MODE_EMOJI } from "../services/travelTimeService";
import { useLearntDefaults, PLATFORM_TO_MEETING_TYPE } from "../hooks/useLearntDefaults";

const LogTimeModal = lazy(() => import('./TimeTracking/LogTimeModal'));


// Types that auto-set the task as private (hidden from team members)
const PERSONAL_TYPES = new Set(["Health", "Family", "Vacation", "Sick", "Personal"]);

// Types that switch the form into Leave Request mode
const LEAVE_REQUEST_TYPES = new Set(["Vacation", "Sick", "Personal"]);

// Maps task type value → backend leave type
const LEAVE_TYPE_MAP = { Vacation: 'vacation', Sick: 'sick', Personal: 'personal' };

// Count Mon–Fri days between two dates (inclusive)
function calcWorkingDays(start, end) {
  if (!start || !end) return 0;
  let count = 0;
  const curr = new Date(start);
  const last = new Date(end);
  curr.setHours(0, 0, 0, 0);
  last.setHours(0, 0, 0, 0);
  while (curr <= last) {
    const d = curr.getDay();
    if (d !== 0 && d !== 6) count++;
    curr.setDate(curr.getDate() + 1);
  }
  return count;
}

function TaskForm({
  title,
  setTitle,
  details,
  setDetails,
  priority,
  setPriority,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  deadlineDate,
  setDeadlineDate,
  deadlineTime,
  setDeadlineTime,
  taskType,
  setTaskType,
  assignedTeam,
  setAssignedTeam,
  householdMemberId,
  setHouseholdMemberId,
  reminderOffset,
  setReminderOffset,
  busy,
  setBusy,
  isPersonal,
  setIsPersonal,
  meetingType,
  setMeetingType,
  location,
  setLocation,
  recurrence,
  setRecurrence,
  recurEditScope,
  setRecurEditScope,
  project,
  setProject,
  editingId,
  onSubmit,
  onCancel,
  taskTypes,
  clashWarning,
  onSaveAnyway,
  onChangeTime,
  open = true,
}) {
  const { currentUser, workspaces, activeWorkspaceId } = useCurrentUser();
  const isPro = userMeetsPlan(currentUser, 'pro'); // FIX-3: shared, case-insensitive, trial-aware
  const [showLogTime, setShowLogTime] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(null);

  // Same derivation as App.js:311 — no new prop threaded, workspaces/activeWorkspaceId
  // are already available via the shared CurrentUserContext.
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';

  const [householdMembers, setHouseholdMembers] = useState([]);

  // Load household members for the Personal/Family "For" picker — mirrors the
  // "Load available users" effect below, just gated to personal workspaces.
  useEffect(() => {
    if (!isPersonalWorkspace) return;
    api('/api/household')
      .then(async res => {
        const d = res.ok ? await res.json() : null;
        setHouseholdMembers(d?.members || []);
      })
      .catch(() => {});
  }, [isPersonalWorkspace]);

  // Validate on every start/end prop change, including when edit form first opens
  // with a pre-inverted task (dateRangeError would otherwise stay null on mount).
  useEffect(() => {
    const s = combineDT(startDate, startTime);
    const e = combineDT(deadlineDate, deadlineTime);
    setDateRangeError(s && e && e < s ? 'End date is before the start date' : null);
  }, [startDate, startTime, deadlineDate, deadlineTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Combines a Date (date part) and a Date (time part) into one comparable Date.
  function combineDT(date, time) {
    if (!date) return null;
    const d = new Date(date);
    if (time) {
      d.setHours(time.getHours(), time.getMinutes(), 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  }

  // Called when the START date picker changes.
  // If the new start would land after the existing end, auto-shift the end by
  // preserving the previous duration (calendar-app convention).
  function handleStartDateChange(newDate) {
    const oldStart = combineDT(startDate, startTime);
    const end      = combineDT(deadlineDate, deadlineTime);
    const newStart = combineDT(newDate, startTime);

    setStartDate(newDate);

    if (newStart && end && newStart > end) {
      const duration = (oldStart && end > oldStart) ? (end - oldStart) : 0;
      const newEnd   = new Date(newStart.getTime() + (duration > 0 ? duration : 0));
      setDeadlineDate(new Date(newEnd));
      if (deadlineTime) setDeadlineTime(new Date(newEnd));
      setDateRangeError(null);
    } else {
      setDateRangeError(null);
    }
  }

  // Called when the START time picker changes — same auto-shift logic.
  function handleStartTimeChange(newTime) {
    const oldStart = combineDT(startDate, startTime);
    const end      = combineDT(deadlineDate, deadlineTime);
    const newStart = combineDT(startDate, newTime);

    setStartTime(newTime);

    if (newStart && end && newStart > end) {
      const duration = (oldStart && end > oldStart) ? (end - oldStart) : 0;
      const newEnd   = new Date(newStart.getTime() + (duration > 0 ? duration : 0));
      setDeadlineDate(new Date(newEnd));
      if (deadlineTime) setDeadlineTime(new Date(newEnd));
      setDateRangeError(null);
    } else {
      setDateRangeError(null);
    }
  }

  // Called when the END date picker changes — show error, never auto-fix.
  function handleDeadlineDateChange(newDate) {
    setDeadlineDate(newDate);
    if (!newDate) { setDateRangeError(null); return; }
    const start  = combineDT(startDate, startTime);
    const newEnd = combineDT(newDate, deadlineTime);
    setDateRangeError(start && newEnd && newEnd < start
      ? 'End date is before the start date'
      : null);
  }

  // Called when the END time picker changes — show error, never auto-fix.
  function handleDeadlineTimeChange(newTime) {
    setDeadlineTime(newTime);
    if (!newTime) return;
    const start  = combineDT(startDate, startTime);
    const newEnd = combineDT(deadlineDate, newTime);
    setDateRangeError(start && newEnd && newEnd < start
      ? 'End date is before the start date'
      : null);
  }

  // Get user's default meeting type preference
  const getUserDefaultMeetingType = () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem("userProfile"));
      return userProfile?.preferences?.defaultMeetingType;
    } catch (error) {
      console.warn("Error parsing user profile:", error);
      return null;
    }
  };

  const defaultMeetingType = getUserDefaultMeetingType();

  // Zoom integration state
  const [isZoomGenerating, setIsZoomGenerating] = useState(false);
  const [zoomConnected, setZoomConnected] = useState(false);

  // Google Meet integration state
  const [isGoogleMeetGenerating, setIsGoogleMeetGenerating] = useState(false);
  const [googleMeetConnected, setGoogleMeetConnected] = useState(false);


  // Teams integration state
  const [isTeamsGenerating, setIsTeamsGenerating] = useState(false);
  const [teamsConnected, setTeamsConnected] = useState(false);

  // Recurrence local state
  const [recurEnabled, setRecurEnabled] = useState(false);
  const [recurFrequency, setRecurFrequency] = useState('daily');
  const [recurInterval, setRecurInterval] = useState(1);
  const [recurUnit, setRecurUnit] = useState('days');
  const [recurDay, setRecurDay] = useState('monday');
  const [recurDate, setRecurDate] = useState(1);
  const [recurEndType, setRecurEndType] = useState('never');
  const [recurCount, setRecurCount] = useState(1);
  const [recurUntil, setRecurUntil] = useState(null);

  // Location autocomplete ref + travel time state
  const [showTravelTime, setShowTravelTime] = useState(false);
  const [travelFrom, setTravelFrom] = useState('');
  const [customTravelAddress, setCustomTravelAddress] = useState('');
  const [travelInfo, setTravelInfo] = useState(null);
  const [travelInfoLoading, setTravelInfoLoading] = useState(false);

  // Initialize recurrence local state from prop (for edit mode)
  useEffect(() => {
    if (recurrence) {
      setRecurEnabled(true);
      const type = (recurrence.type || recurrence.frequency || 'daily').toLowerCase();
      setRecurFrequency(type);
      setRecurInterval(recurrence.interval || 1);
      if (recurrence.day) setRecurDay(recurrence.day.toLowerCase());
      if (recurrence.date) setRecurDate(recurrence.date);
      if (recurrence.unit) setRecurUnit(recurrence.unit);
      if (recurrence.count) { setRecurEndType('count'); setRecurCount(recurrence.count); }
      else if (recurrence.until) { setRecurEndType('until'); setRecurUntil(new Date(recurrence.until)); }
      else setRecurEndType('never');
    } else {
      setRecurEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local recurrence state -> parent recurrence prop
  useEffect(() => {
    if (!setRecurrence) return;
    if (!recurEnabled) {
      setRecurrence(null);
      return;
    }
    const obj = { type: recurFrequency, interval: recurFrequency === 'custom' ? recurInterval : 1 };
    if (recurFrequency === 'weekly') obj.day = recurDay;
    if (recurFrequency === 'monthly') obj.date = recurDate;
    if (recurFrequency === 'custom') obj.unit = recurUnit;
    if (recurEndType === 'count') obj.count = recurCount;
    if (recurEndType === 'until' && recurUntil) obj.until = recurUntil.toISOString();
    setRecurrence(obj);
  }, [recurEnabled, recurFrequency, recurInterval, recurUnit, recurDay, recurDate, recurEndType, recurCount, recurUntil, setRecurrence]);

  // Auto-set isPersonal when a personal task type is selected
  useEffect(() => {
    if (setIsPersonal) setIsPersonal(PERSONAL_TYPES.has(taskType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskType]);

  // Load projects for the project dropdown
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    try {
      const { default: projectManager } = require("../models/projects");
      setProjects(projectManager.getAllProjects());
    } catch { /* a failed projects load is not 'you have no projects' */ }
  }, []);


  // Team assignment roster — active workspace's people (business → team members).
  const { members: availableUsers } = useWorkspaceRoster();
  const [selectedUsers, setSelectedUsers] = useState([]);

  // ✅ @MENTION FUNCTIONALITY STATE
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [detailsWithMentions, setDetailsWithMentions] = useState(details || '');

  // Mention resolution now runs against the active-workspace roster (availableUsers);
  // notifyUserMentioned stays on the context (unchanged contract).
  const { notifyUserMentioned } = useCurrentUser();

  // Sync selectedUsers from assignedTeam. Ids are real user UUIDs (strings) —
  // carry them as-is; the old parseInt coercion (mock numeric ids) would drop
  // every real assignee on edit.
  useEffect(() => {
    if (assignedTeam && Array.isArray(assignedTeam)) {
      setSelectedUsers(assignedTeam.map(String));
    } else {
      setSelectedUsers([]);
    }
  }, [assignedTeam]);

  // Team assignment handler (Assign to and Invite both write assignedTeam through it)
  const handleTeamAssignmentChange = (selectedOptions) => {
    const userIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedUsers(userIds);
    setAssignedTeam(userIds);
  };

  // ✅ @MENTION HANDLING METHODS
  const handleDescriptionChange = (e) => {
    const newText = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setDetailsWithMentions(newText);
    setDetails(newText);

    // Check for @mention trigger
    const textBeforeCursor = newText.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9\s]*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      setMentionStartIndex(cursorPosition - mentionMatch[0].length);

      // Get mention suggestions
      const suggestions = rosterMentionSuggestions(availableUsers, query);
      setMentionSuggestions(suggestions);
      setShowMentionDropdown(suggestions.length > 0);
    } else {
      setShowMentionDropdown(false);
      setMentionSuggestions([]);
      setMentionQuery('');
      setMentionStartIndex(-1);
    }
  };

  const handleMentionSelect = (selectedUser) => {
    if (mentionStartIndex === -1) return;

    const beforeMention = detailsWithMentions.substring(0, mentionStartIndex);
    const afterCursor = detailsWithMentions.substring(mentionStartIndex + mentionQuery.length + 1);
    const mentionText = `@${selectedUser.name}`;

    const newText = beforeMention + mentionText + afterCursor;
    setDetailsWithMentions(newText);
    setDetails(newText);

    // Also add to assigned team if not already assigned
    if (!selectedUsers.includes(selectedUser.id)) {
      setSelectedUsers(prev => [...prev, selectedUser.id]);
    }

    // Hide dropdown
    setShowMentionDropdown(false);
    setMentionSuggestions([]);
    setMentionQuery('');
    setMentionStartIndex(-1);
  };

  const handleMentionKeyDown = (e) => {
    if (!showMentionDropdown || mentionSuggestions.length === 0) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
    } else if (e.key === 'Enter' && mentionSuggestions.length > 0) {
      e.preventDefault();
      handleMentionSelect(mentionSuggestions[0]);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
      setMentionSuggestions([]);
    }
  };

  // Process mentions on form submission
  const processMentionsOnSubmit = () => {
    const mentions = rosterParseMentions(availableUsers, detailsWithMentions);

    // Notify mentioned users
    mentions.forEach(mention => {
      if (mention.userId !== currentUser?.id) {
        notifyUserMentioned(mention.userId, {
          type: 'task',
          title: title || 'New Task',
          description: detailsWithMentions,
          assignedBy: currentUser?.name
        });
      }
    });

    return mentions;
  };

  // Block D2 fix: connection status comes from the SERVER (VideoConnection) — the
  // same source the Settings > Video meetings page uses — not client localStorage.
  // The old isZoomConnected()/isGoogleMeetAvailable()/isTeamsConnected() checks read
  // localStorage, which the server-side OAuth flow never populates, so every provider
  // falsely showed "not connected". Fetched once on mount for all three.
  useEffect(() => {
    let cancelled = false;
    api('/api/connections/video')
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        const list = Array.isArray(d.connections) ? d.connections : [];
        const have = (p) => list.some(c => c.provider === p);
        setZoomConnected(have('zoom'));
        setGoogleMeetConnected(have('meet'));
        setTeamsConnected(have('teams'));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Handle Zoom meeting generation
  const handleGenerateZoomLink = async () => {
    if (!zoomConnected) {
      alert('Connect Zoom first in Settings → Video meetings.');
      return;
    }

    setIsZoomGenerating(true);
    try {
      // Server-side create via the stored VideoConnection (Block D2 fix), not the
      // deprecated client/localStorage path.
      const start = combineDT(startDate, startTime) || new Date();
      const pad = n => String(n).padStart(2, '0');
      const startISO = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}:00`;
      const res = await api('/api/connections/video/zoom/create-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: title || 'Aidey Meeting', startTime: startISO, durationMinutes: 60 }),
      });
      const meeting = await res.json();
      if (!res.ok || !meeting.joinUrl) throw new Error(meeting.error || 'Zoom returned no link');
      setLocation(meeting.joinUrl);
      alert(`Zoom meeting created. Meeting ID: ${meeting.meetingId}`);
    } catch (error) {
      console.error('Error generating Zoom link:', error);
      showErrorToast(`Failed to create Zoom meeting: ${error.message}`);
    } finally {
      setIsZoomGenerating(false);
    }
  };

  // Handle Google Meet generation
  const handleGenerateGoogleMeetLink = async () => {
    if (!googleMeetConnected) {
      alert('Please connect your Google Calendar first to enable Google Meet generation.');
      return;
    }

    setIsGoogleMeetGenerating(true);
    try {
      // Server-side (Block D5): creates a real Google Calendar event with
      // conferenceData so Google mints a Meet link. Uses combineDT so the actual
      // picked date+time is sent (the old client path fed a Date into a string
      // template → "Invalid Date").
      const start = combineDT(startDate, startTime) || new Date();
      const pad = n => String(n).padStart(2, '0');
      const startISO = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}:00`;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await api('/api/connections/video/meet/create-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: title || 'Aidey Meeting', startTime: startISO, durationMinutes: 60, timeZone }),
      });
      const meeting = await res.json();
      if (!res.ok || !meeting.meetLink) throw new Error(meeting.error || 'No Meet link returned');
      setLocation(meeting.meetLink);
      alert('Google Meet created and added to your Google Calendar.');
    } catch (error) {
      console.error('Error generating Google Meet link:', error);
      showErrorToast(`Failed to create Google Meet: ${error.message}`);
    } finally {
      setIsGoogleMeetGenerating(false);
    }
  };

  // Handle Teams meeting generation
  const handleGenerateTeamsLink = async () => {
    if (!teamsConnected) {
      alert('Please connect your Microsoft Outlook account first to enable Teams generation.');
      return;
    }

    setIsTeamsGenerating(true);
    try {
      const meetingDetails = {
        title: title || "Aidey Meeting",
        date: startDate?.toISOString().split('T')[0],
        time: startTime,
        duration: 60, // Default 1 hour
        details: details || `Microsoft Teams meeting for: ${title || "Aidey Meeting"}`
      };

      const meeting = await createTeamsMeeting(meetingDetails);
      if (meeting.success && meeting.meetingLink) {
        setLocation(meeting.meetingLink);
        alert(`Microsoft Teams meeting created successfully! Meeting added to your Outlook calendar.`);
      } else {
        throw new Error(meeting.error || 'Failed to create Teams meeting');
      }
    } catch (error) {
      console.error('Error generating Teams link:', error);
      showErrorToast(`Failed to create Teams meeting: ${error.message}`);
    } finally {
      setIsTeamsGenerating(false);
    }
  };

  // Validation function for location based on meeting type
  const validateLocation = () => {
    if (!meetingType || !location) return true; // No validation needed if no meeting type or location

    if (meetingType === "Address") {
      return location.trim().length > 0;
    }

    if (["Zoom", "Google Meet", "Teams", "Custom Link"].includes(meetingType)) {
      // Basic URL validation
      try {
        new URL(location);
        return true;
      } catch {
        return false;
      }
    }

    return true;
  };

  // Leave request mode state
  const isLeaveMode = LEAVE_REQUEST_TYPES.has(taskType);

  // Carla, 2026-10-12: her learnt defaults (Aidey Intelligence; hooks/useLearntDefaults). A NEW task
  // starts on her usual priority, marked as Aidey's suggestion (Suggest) or as set by Aidey (Auto),
  // until she picks one herself; editing never changes a task's priority. The server applies the
  // same rule wherever a task is made with no priority (Aidey Backend/lib/learntDefaults.js).
  const learnt = useLearntDefaults(!editingId);
  const [priorityPicked, setPriorityPicked] = useState(false);
  const learntPriority = !editingId && !isLeaveMode ? learnt?.taskPriority : null;
  useEffect(() => {
    if (!learntPriority?.priority || priorityPicked) return;
    if (priority !== learntPriority.priority) setPriority(learntPriority.priority);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learntPriority?.priority, priorityPicked]);
  const priorityByAidey = !!learntPriority && !priorityPicked && priority === learntPriority.priority;
  const pickPriority = (v) => { setPriorityPicked(true); setPriority(v); };
  const learntPlatform = !editingId ? learnt?.meetingPlatform : null;
  const learntMeetingType = learntPlatform ? PLATFORM_TO_MEETING_TYPE[learntPlatform.platform] : null;
  const [leaveAllowance, setLeaveAllowance] = useState(null);
  const [isLeaveLoadingAllowance, setIsLeaveLoadingAllowance] = useState(false);

  useEffect(() => {
    if (!isLeaveMode) { setLeaveAllowance(null); return; }
    setIsLeaveLoadingAllowance(true);
    api('/api/leave/allowance')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setLeaveAllowance(data); })
      .catch(() => {})
      .finally(() => setIsLeaveLoadingAllowance(false));
  }, [isLeaveMode]);

  const workingDays = isLeaveMode ? calcWorkingDays(startDate, deadlineDate) : 0;

  // Travel time auto-lookup when location + travelFrom are set
  useEffect(() => {
    if (!showTravelTime || !location) { setTravelInfo(null); return; }
    const travelPrefs = currentUser?.preferences?.travel || {};
    let origin = '';
    if (travelFrom === 'home') origin = travelPrefs.homeAddress || '';
    else if (travelFrom === 'office') origin = travelPrefs.workplaceAddress || '';
    else if (travelFrom === 'custom') origin = customTravelAddress;
    if (!origin) { setTravelInfo(null); return; }
    let cancelled = false;
    setTravelInfoLoading(true);
    setTravelInfo(null);
    getTravelInfo(origin, location)
      .then(result => { if (!cancelled) { setTravelInfo(result); setTravelInfoLoading(false); } })
      .catch(() => { if (!cancelled) setTravelInfoLoading(false); });
    return () => { cancelled = true; };
  }, [location, travelFrom, customTravelAddress, showTravelTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending attachments (create mode — queued until task ID is known)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [linkInput, setLinkInput] = useState('');
  const pendingFileInputRef = useRef(null);

  const handleAddPendingLink = () => {
    const val = linkInput.trim();
    if (!val) return;
    setPendingLinks(prev => [...prev, val]);
    setLinkInput('');
  };

  // Attachments (edit mode only). Two places, one list: files uploaded now live in the private file
  // store (POST /api/files, kind 'task'; Carla's decision 2), because the old multipart upload was
  // refused at the edge for any real file. Older rows (links, and the few uploads that got through)
  // stay in task_attachments and are listed, opened and removed as before. A stored file is
  // `{ stored: true, id, fileName }`.
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(new Set());

  useEffect(() => {
    if (!editingId) { setTaskAttachments([]); return; }
    let cancelled = false;
    Promise.all([
      api(`/api/tasks/${editingId}/attachments`).then(r => r.json()),
      api(`/api/files?kind=task&entityId=${encodeURIComponent(editingId)}`).then(r => r.json()),
    ]).then(([older, stored]) => {
      if (cancelled) return;
      const files = (Array.isArray(stored.files) ? stored.files : []).map(f => ({ ...f, stored: true }));
      setTaskAttachments([...files, ...(Array.isArray(older.attachments) ? older.attachments : [])]);
    }).catch((err) => { if (!cancelled) showErrorToast(messageFromError(err, "This task's files could not be loaded.")); });
    return () => { cancelled = true; };
  }, [editingId]);

  // Upload each file to the store against this task. Resolves to the files that were saved; each
  // refusal (too big, a type not accepted, not allowed) is said, with the server's own reason.
  const uploadTaskFiles = async (taskId, files) => {
    const saved = [];
    for (const file of files) {
      const tooBig = fileTooBig(file);
      if (tooBig) { showErrorToast(tooBig); continue; }
      setAttachmentUploading(prev => new Set([...prev, file.name]));
      try {
        saved.push({ ...(await uploadStoredFile({ kind: 'task', entityId: taskId, file })), stored: true });
      } catch (err) {
        showErrorToast(messageFromError(err, `"${file.name}" was not attached.`));
      } finally {
        setAttachmentUploading(prev => { const s = new Set(prev); s.delete(file.name); return s; });
      }
    }
    return saved;
  };

  const handleAttachFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !editingId) return;
    e.target.value = '';
    const saved = await uploadTaskFiles(editingId, files);
    if (saved.length) setTaskAttachments(prev => [...saved, ...prev]);
  };

  const handleDeleteAttachment = async (a) => {
    try {
      await api(a.stored ? `/api/files/${encodeURIComponent(a.id)}` : `/api/tasks/${editingId}/attachments/${a.id}`, { method: 'DELETE' });
      setTaskAttachments(prev => prev.filter(x => !(x.id === a.id && !!x.stored === !!a.stored)));
    } catch (err) {
      showErrorToast(messageFromError(err, `"${a.fileName}" was not removed.`));
    }
  };

  // A stored file, or an older private upload (storageKey), opens by a link signed at the moment of
  // opening. Older public or external rows keep their href.
  const openAttachment = async (a) => {
    try {
      if (a.stored) { await openStoredFile(a.id); return; }
      const res = await api(`/api/tasks/${editingId}/attachments/${a.id}/url`);
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      showErrorToast(messageFromError(err, `"${a.fileName}" could not be opened.`));
    }
  };

  // ✅ Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Leave request submit handler
  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!startDate || !deadlineDate) {
      alert('Please select both a start date and end date for your leave request.');
      return;
    }
    if (workingDays <= 0) {
      alert('The selected date range contains no working days (Mon–Fri).');
      return;
    }
    if (leaveAllowance && taskType !== 'Sick' && workingDays > leaveAllowance.remaining) {
      // eslint-disable-next-line no-restricted-globals
      const ok = confirm(`You only have ${leaveAllowance.remaining} days remaining but are requesting ${workingDays}. Submit anyway?`);
      if (!ok) return;
    }

    setIsSubmitting(true);
    try {
      const res = await api('/api/leave/request', {
        method: 'POST',
        body: JSON.stringify({
          type: LEAVE_TYPE_MAP[taskType] || 'vacation',
          startDate: startDate.toISOString().split('T')[0],
          endDate: deadlineDate.toISOString().split('T')[0],
          daysRequested: workingDays,
          note: details || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showErrorToast(data.error || 'Failed to submit leave request');
        return;
      }
      alert(`Your ${taskType.toLowerCase()} request has been submitted. Your manager will review it shortly.`);
      if (onCancel) onCancel();
    } catch {
      showErrorToast('Failed to submit leave request. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced form submit handler with validation and loading state
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Validate location if meeting type is selected
      if (meetingType && location) {
        if (!validateLocation()) {
          if (meetingType === "Address") {
            // eslint-disable-next-line no-restricted-globals
            alert("Please enter a valid address for the physical location.");
            return;
          } else {
            // eslint-disable-next-line no-restricted-globals
            alert(`Please enter a valid URL for the ${meetingType} meeting link.`);
            return;
          }
        }
      }

      // If meeting type is selected but no location provided, show warning
      if (meetingType && !location) {
        // eslint-disable-next-line no-restricted-globals
        const shouldContinue = confirm(
          `You've selected "${meetingType}" as the meeting type but haven't provided a location. Continue without location?`
        );
        if (!shouldContinue) return;
      }

      // ✅ Process @mentions before submission
      const mentions = processMentionsOnSubmit();
      console.log('Task mentions processed:', mentions);

      // Pre-generate the task ID so pending attachments can be uploaded after creation
      const preId = (!editingId && (pendingFiles.length > 0 || pendingLinks.length > 0))
        ? Date.now()
        : null;
      if (preId) e._aideyTaskId = preId;

      // Call the original onSubmit handler
      await onSubmit(e);

      // Upload any pending attachments now that the task exists in the DB
      if (preId) {
        await uploadTaskFiles(preId, pendingFiles);
        for (const link of pendingLinks) {
          try {
            await api(`/api/tasks/${preId}/attachments`, {
              method: 'POST',
              body: JSON.stringify({ source: 'url', externalUrl: link }),
            });
          } catch (err) { showErrorToast(messageFromError(err, "Could not pre id.")); }
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // eslint-disable-next-line no-restricted-globals
      showErrorToast('An error occurred while saving the task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMeetingToggle = (e) => {
    if (e.target.checked) {
      if (defaultMeetingType && !["Custom Link", "Address"].includes(defaultMeetingType)) {
        setMeetingType(defaultMeetingType);
      } else if (learntMeetingType) {
        // No saved default: her usual platform (connected; the server only returns one that is).
        setMeetingType(learntMeetingType);
      } else {
        setMeetingType(googleMeetConnected ? "Google Meet" : zoomConnected ? "Zoom" : teamsConnected ? "Teams" : "Google Meet");
      }
    } else {
      setMeetingType(null);
    }
  };

  // ── UI-5: the v2 layout (mockup add-task-modal.html). Everything above is unchanged: the same
  // state, the same handlers, the same submit. Only how it is shown is new. ─────────────────────
  const typeOptions = taskTypeOptions(taskTypes);
  const pad2 = (n) => String(n).padStart(2, '0');
  const toDateStr = (d) => (d ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` : '');
  const fromDateStr = (s) => { if (!s) return null; const [y, m, dd] = s.split('-').map(Number); return new Date(y, m - 1, dd); };
  const toTimeStr = (t) => (t ? `${pad2(t.getHours())}:${pad2(t.getMinutes())}` : '');
  const fromTimeStr = (s) => { if (!s) return null; const [h, mi] = s.split(':').map(Number); const d = new Date(); d.setHours(h, mi, 0, 0); return d; };

  const start = combineDT(startDate, startTime);
  const end = combineDT(deadlineDate, deadlineTime);
  const allDay = !!startDate && !startTime;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const plusDays = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };
  const sameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const nextMonday = (() => { const d = plusDays(1); while (d.getDay() !== 1) d.setDate(d.getDate() + 1); return d; })();

  // A new task opens on today, the next whole hour, for an hour (the mockup's default), unless
  // the entry point already chose a day (a calendar day's "+ Add task").
  useEffect(() => {
    if (editingId || startTime || isLeaveMode) return;
    const s = new Date(); s.setMinutes(0, 0, 0); s.setHours(s.getHours() + 1);
    const day = startDate ? new Date(startDate) : new Date(s);
    day.setHours(0, 0, 0, 0);
    const st = new Date(day); st.setHours(s.getHours(), 0, 0, 0);
    setStartDate(day); setStartTime(st);
    const e = new Date(st.getTime() + 60 * 60000);
    setDeadlineDate(new Date(e)); setDeadlineTime(new Date(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DURATIONS = [{ value: 15, label: '15 min' }, { value: 30, label: '30 min' }, { value: 60, label: '1 hour' }, { value: 90, label: '1½ hours' }, { value: 120, label: '2 hours' }];
  const durationMins = start && end && sameDay(start, end) ? Math.round((end - start) / 60000) : null;
  const [customEnd, setCustomEnd] = useState(false);
  const durationValue = customEnd || (end && !DURATIONS.some((d) => d.value === durationMins)) ? 'custom' : durationMins;
  const setDuration = (mins) => {
    if (mins === 'custom') { setCustomEnd(true); return; }
    setCustomEnd(false);
    const s = start || combineDT(startDate || today, fromTimeStr('09:00'));
    const e = new Date(s.getTime() + mins * 60000);
    setDeadlineDate(new Date(e)); setDeadlineTime(new Date(e)); setDateRangeError(null);
  };
  const pickDay = (day) => {
    const keep = durationMins;
    handleStartDateChange(day);
    if (allDay || isLeaveMode) { if (!deadlineDate || deadlineDate < day || !isLeaveMode) setDeadlineDate(new Date(day)); return; }
    if (startTime && keep != null) {
      const s = combineDT(day, startTime); const e = new Date(s.getTime() + keep * 60000);
      setDeadlineDate(new Date(e)); setDeadlineTime(new Date(e));
    }
  };
  const setAllDay = (on) => {
    if (on) { setStartTime(null); setDeadlineTime(null); setDeadlineDate(startDate ? new Date(startDate) : null); setCustomEnd(false); return; }
    const st = fromTimeStr('09:00'); setStartTime(st);
    const e = combineDT(startDate || today, fromTimeStr('10:00'));
    setDeadlineDate(new Date(e)); setDeadlineTime(new Date(e));
  };
  const dayChoice = !startDate ? null : sameDay(startDate, today) ? 'today' : sameDay(startDate, plusDays(1)) ? 'tomorrow' : sameDay(startDate, nextMonday) ? 'nextweek' : null;
  const whenSummary = !start ? 'Not set' : allDay
    ? (end && !sameDay(start, end) ? `${formatTaskDay(start)} to ${formatTaskDay(end)}` : `${formatTaskDay(start)}, all day`)
    : `${formatTaskDay(start)}, ${formatTaskClock(start)}${end ? ` to ${sameDay(start, end) ? formatTaskClock(end) : `${formatTaskDay(end)} ${formatTaskClock(end)}`}` : ''}`;

  const REMINDERS = [[0, 'At the time'], [5, '5 min'], [10, '10 min'], [15, '15 min'], [30, '30 min'], [60, '1 hour'], [120, '2 hours'], [1440, '1 day'], [2880, '2 days']].map(([value, label]) => ({ value, label }));
  const REPEATS = [['never', 'Never'], ['daily', 'Daily'], ['weekday', 'Weekdays'], ['weekly', 'Weekly'], ['monthly', 'Monthly'], ['yearly', 'Yearly'], ['custom', 'Custom']].map(([value, label]) => ({ value, label }));
  const repeatValue = recurEnabled ? recurFrequency : 'never';
  const setRepeat = (v2) => { if (v2 === 'never') { setRecurEnabled(false); return; } setRecurEnabled(true); setRecurFrequency(v2); };

  // Where: in person (an address), video (a meeting link), or nowhere.
  const VIDEO = ['Google Meet', 'Zoom', 'Teams'];
  const derivedWhere = meetingType && VIDEO.includes(meetingType) ? 'video' : location ? 'place' : 'none';
  const [whereMode, setWhereMode] = useState(derivedWhere);
  const chooseWhere = (mode) => {
    setWhereMode(mode);
    if (mode === 'none') { setLocation(''); setMeetingType(null); setShowTravelTime(false); }
    else if (mode === 'place') { if (meetingType && VIDEO.includes(meetingType)) setLocation(''); setMeetingType(null); }
    else if (!meetingType || !VIDEO.includes(meetingType)) { if (location && !/^https?:/i.test(location)) setLocation(''); handleMeetingToggle({ target: { checked: true } }); }
  };
  const generating = isZoomGenerating || isGoogleMeetGenerating || isTeamsGenerating;
  const connected = meetingType === 'Zoom' ? zoomConnected : meetingType === 'Teams' ? teamsConnected : googleMeetConnected;
  const generate = () => {
    if (meetingType === 'Zoom') handleGenerateZoomLink();
    else if (meetingType === 'Google Meet') handleGenerateGoogleMeetLink();
    else if (meetingType === 'Teams') handleGenerateTeamsLink();
  };

  // Assign to (people and departments) and Invite (anyone by email) share assignedTeam, as before.
  const memberIds = new Set(availableUsers.map((u) => String(u.id)));
  const assignedIds = selectedUsers.filter((x) => memberIds.has(String(x)));
  const invitedEmails = selectedUsers.filter((x) => !memberIds.has(String(x)));
  const writeTeam = (ids, emails) => handleTeamAssignmentChange([...ids, ...emails].map((value) => ({ value })));
  const [inviteText, setInviteText] = useState('');
  const [inviteError, setInviteError] = useState(null);
  const addInvite = () => {
    const val = inviteText.trim().replace(/,$/, '');
    if (!val) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { setInviteError('Enter an email address, like name@example.com.'); return; }
    if (!invitedEmails.includes(val)) writeTeam(assignedIds, [...invitedEmails, val]);
    setInviteText(''); setInviteError(null);
  };

  const fileCount = editingId ? taskAttachments.length : pendingFiles.length + pendingLinks.length;
  const [more, setMore] = useState(() => !!(details || invitedEmails.length || editingId));
  const moreSummary = [details ? 'Notes' : null, invitedEmails.length ? `${invitedEmails.length} invited` : null, fileCount ? `${fileCount} ${fileCount === 1 ? 'file' : 'files'}` : null].filter(Boolean).join(' · ') || 'Optional';

  const [another, setAnother] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  // Say what is missing AT the field, before App's own check (which still runs).
  const submit = (e) => {
    const errs = {};
    if (!title || !title.trim()) errs.title = 'Give the task a title.';
    if (!startDate) errs.when = 'Choose a day.';
    else if (!editingId && !isLeaveMode && !deadlineDate) errs.when = 'Choose how long it takes, or an end.';
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      e.preventDefault();
      document.getElementById(errs.title ? 'task-title' : 'task-when')?.focus?.();
      return undefined;
    }
    if (another && !editingId) e._aideyAnother = true;
    return isLeaveMode ? handleLeaveSubmit(e) : handleSubmit(e);
  };

  const heading = isLeaveMode ? `Request ${taskType === 'Sick' ? 'sick leave' : 'time off'}` : editingId ? 'Edit task' : 'Add task';
  const saveLabel = isSubmitting
    ? (isLeaveMode ? 'Sending…' : editingId ? 'Saving…' : 'Adding…')
    : (isLeaveMode ? 'Send request' : editingId ? 'Save changes' : 'Add task');

  const footer = (
    <div className="ui-taskform__foot">
      {clashWarning && (
        <Banner tone="notice" action={(
          <span className="ui-taskform__clashbtns">
            <Button variant="secondary" size="sm" onClick={onChangeTime}>Change time</Button>
            <Button variant="confirm" size="sm" onClick={onSaveAnyway}>Save anyway</Button>
          </span>
        )}>
          This overlaps with "{clashWarning.conflictTitle}"{clashWarning.conflictTime ? ` at ${clashWarning.conflictTime}` : ''}.
        </Banner>
      )}
      <div className="ui-taskform__buttons">
        {!editingId && !isLeaveMode && <Checkbox checked={another} onChange={setAnother} label="Add another after this" />}
        <span className="ui-taskform__spacer" />
        {isPro && editingId && !isLeaveMode && <Button variant="tertiary" onClick={() => setShowLogTime(true)}>⏱ Log time</Button>}
        {onCancel && <Button variant="tertiary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>}
        <Button variant="confirm" type="submit" form="task-form" disabled={isSubmitting || !!dateRangeError}>{saveLabel}</Button>
      </div>
    </div>
  );

  const priorityOptions = [
    { value: 'Low', label: 'Low', color: 'priority-low' },
    { value: 'Medium', label: 'Medium', color: 'priority-medium' },
    { value: 'High', label: 'High', color: 'priority-high', dot: true },   // decision 50: High has the dot
  ];

  return (
    <Modal open={open} onClose={onCancel} title={heading} color="highlight" width={720} footer={footer}>
      <form id="task-form" className="ui-taskform" onSubmit={submit} noValidate>
        <div className="ui-taskform__top">
          <TitleInput id="task-title" data-autofocus value={title} onChange={(e) => { setTitle(e.target.value); if (fieldErrors.title) setFieldErrors((f) => ({ ...f, title: null })); }}
            placeholder={isLeaveMode ? 'What is it for? (optional)' : 'What needs doing?'} aria-label="Task title"
            aria-invalid={fieldErrors.title ? true : undefined} aria-describedby={fieldErrors.title ? 'task-title-error' : undefined} />
          {fieldErrors.title && <p id="task-title-error" className="ui-field__error">{fieldErrors.title}</p>}
          <div className="ui-taskform__typerow">
            <div className="ui-taskform__inline">
              <span className="ui-taskform__eyebrow" aria-hidden="true">Type</span>
              <RichDropdown label="Type" options={typeOptions} value={taskType} onChange={setTaskType} placeholder="Choose a type" />
            </div>
            <div className="ui-taskform__inline ui-taskform__priority">
              <span className="ui-taskform__eyebrow" aria-hidden="true">Priority</span>
              <SegmentedControl label="Priority" options={priorityOptions} value={priority} onChange={pickPriority} />
            </div>
          </div>
          {priorityByAidey && (
            <p className="ui-taskform__hint">
              {learntPriority.mode === 'auto' ? 'Your usual priority, set by Aidey. Change it if this one is different.' : "Aidey's suggestion: your usual priority. Change it if you like."}
            </p>
          )}
          {PERSONAL_TYPES.has(taskType) && <p className="ui-taskform__note">🔒 Private: only you and managers can see this task.</p>}
        </div>

        {!isLeaveMode && (isPersonalWorkspace ? (
          <section className="ui-taskform__group" aria-labelledby="task-for-label">
            <span id="task-for-label" className="ui-taskform__eyebrow">For</span>
            {householdMembers.length > 0 ? (
              <ChipGroup label="For" scroll={false} value={householdMemberId || ''} onChange={(val) => setHouseholdMemberId(val || null)}
                options={[{ value: '', label: 'Whole household', icon: '🏠' }, ...householdMembers.map((m) => ({ value: m.id, label: m.name }))]} />
            ) : (
              <p className="ui-taskform__hint">For the whole household. Add people in Family to give a task to one of them.</p>
            )}
          </section>
        ) : (
          <section className="ui-taskform__group" aria-labelledby="task-assign-label">
            <span id="task-assign-label" className="ui-taskform__eyebrow">Assign to</span>
            <AssignPicker members={availableUsers} value={assignedIds} currentUserId={currentUser?.id}
              onChange={(ids) => writeTeam(ids, invitedEmails)} />
          </section>
        ))}

        {!isLeaveMode && projects.length > 0 && (
          <section className="ui-taskform__group" aria-labelledby="task-project-label">
            <span id="task-project-label" className="ui-taskform__eyebrow">Client / project</span>
            <ChipGroup label="Client or project" value={project || ''} onChange={(val) => setProject && setProject(val || null)}
              options={[{ value: '', label: 'No project' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
          </section>
        )}

        <article className="ui-taskform__card" aria-labelledby="task-when-label">
          <header className="ui-taskform__cardhead">
            <span id="task-when-label" className="ui-taskform__eyebrow">When</span>
            <span className="ui-taskform__summary">{whenSummary}</span>
          </header>
          <div className="ui-taskform__cardbody">
            <div className="ui-taskform__row">
              <ChipGroup label="Day" scroll={false} value={dayChoice} onChange={(val) => pickDay(val === 'today' ? today : val === 'tomorrow' ? plusDays(1) : nextMonday)}
                options={[{ value: 'today', label: 'Today' }, { value: 'tomorrow', label: 'Tomorrow' }, { value: 'nextweek', label: 'Next week' }]} />
              <DateInput id="task-when" aria-label={isLeaveMode ? 'First day' : 'Day'} className="ui-taskform__date" value={toDateStr(startDate)}
                onChange={(e) => pickDay(fromDateStr(e.target.value))} aria-invalid={fieldErrors.when ? true : undefined} />
              {!isLeaveMode && <span className="ui-taskform__push"><Toggle checked={allDay} onChange={setAllDay} label="All day" /></span>}
            </div>
            {!allDay && !isLeaveMode && (
              <div className="ui-taskform__row">
                <TimeInput aria-label="Start time" className="ui-taskform__time" value={toTimeStr(startTime)}
                  onChange={(e) => { const t = fromTimeStr(e.target.value); const keep = durationMins; handleStartTimeChange(t); if (t && keep != null && !customEnd) { const s = combineDT(startDate || today, t); const en = new Date(s.getTime() + keep * 60000); setDeadlineDate(new Date(en)); setDeadlineTime(new Date(en)); } }} />
                <span className="ui-taskform__for">for</span>
                <ChipGroup label="How long" scroll={false} value={durationValue} onChange={setDuration}
                  options={[...DURATIONS, { value: 'custom', label: 'Other end…' }]} />
              </div>
            )}
            {(isLeaveMode || allDay || durationValue === 'custom') && (
              <div className="ui-taskform__row">
                <span className="ui-taskform__for">{isLeaveMode ? 'Last day' : 'Ends'}</span>
                <DateInput aria-label={isLeaveMode ? 'Last day' : 'End day'} className="ui-taskform__date" value={toDateStr(deadlineDate)}
                  onChange={(e) => handleDeadlineDateChange(fromDateStr(e.target.value))} />
                {!allDay && !isLeaveMode && (
                  <TimeInput aria-label="End time" className="ui-taskform__time" value={toTimeStr(deadlineTime)}
                    onChange={(e) => handleDeadlineTimeChange(fromTimeStr(e.target.value))} />
                )}
              </div>
            )}
            {(fieldErrors.when || dateRangeError) && <p className="ui-field__error" role="alert">{fieldErrors.when || dateRangeError}</p>}

            {isLeaveMode && (
              <div className="ui-taskform__leave">
                {isLeaveLoadingAllowance ? <span>Loading your allowance…</span> : leaveAllowance ? (
                  taskType === 'Sick' ? (
                    <span>Sick days are tracked separately and do not come out of your holiday allowance.{workingDays > 0 && <strong> {workingDays} working day{workingDays !== 1 ? 's' : ''} selected.</strong>}</span>
                  ) : (
                    <span>
                      <strong>{leaveAllowance.remaining} day{leaveAllowance.remaining !== 1 ? 's' : ''} left</strong> of {leaveAllowance.totalAllowance}
                      {leaveAllowance.usedDays > 0 && ` · ${leaveAllowance.usedDays} used`}{leaveAllowance.pendingDays > 0 && ` · ${leaveAllowance.pendingDays} waiting`}
                      {workingDays > 0 && <> · This request: <strong>{workingDays} working day{workingDays !== 1 ? 's' : ''}</strong>{leaveAllowance.remaining < workingDays && ', more than you have left'}</>}
                    </span>
                  )
                ) : <span>Join a team to see your holiday allowance.</span>}
              </div>
            )}

            {!isLeaveMode && (
              <>
                <div className="ui-taskform__pair">
                  <div className="ui-taskform__sub">
                    <span className="ui-taskform__label" id="task-remind-label">Remind me</span>
                    <ChipGroup label="Remind me" scroll={false} value={Number(reminderOffset)} onChange={(val) => setReminderOffset(val)} options={REMINDERS} />
                  </div>
                  <div className="ui-taskform__sub">
                    <span className="ui-taskform__label">Repeat</span>
                    <ChipGroup label="Repeat" scroll={false} value={repeatValue} onChange={setRepeat} options={REPEATS} />
                  </div>
                </div>
                {recurEnabled && (
                  <div className="ui-taskform__recur">
                    {recurFrequency === 'custom' && (
                      <label className="ui-taskform__recurrow">Every
                        <Input type="number" min="1" max="99" className="ui-taskform__num" value={recurInterval} onChange={(e) => setRecurInterval(Math.max(1, parseInt(e.target.value, 10) || 1))} />
                        <Select aria-label="Unit" value={recurUnit} onChange={(e) => setRecurUnit(e.target.value)} options={[{ value: 'days', label: 'days' }, { value: 'weeks', label: 'weeks' }, { value: 'months', label: 'months' }]} />
                      </label>
                    )}
                    {recurFrequency === 'weekly' && (
                      <label className="ui-taskform__recurrow">On
                        <Select value={recurDay} onChange={(e) => setRecurDay(e.target.value)}
                          options={['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => ({ value: d, label: d[0].toUpperCase() + d.slice(1) }))} />
                      </label>
                    )}
                    {recurFrequency === 'monthly' && (
                      <label className="ui-taskform__recurrow">On day
                        <Input type="number" min="1" max="31" className="ui-taskform__num" value={recurDate} onChange={(e) => setRecurDate(Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)))} />
                        of the month
                      </label>
                    )}
                    <div className="ui-taskform__recurrow">
                      <span>Ends</span>
                      <Select aria-label="Repeat ends" value={recurEndType} onChange={(e) => setRecurEndType(e.target.value)}
                        options={[{ value: 'never', label: 'Never' }, { value: 'count', label: 'After' }, { value: 'until', label: 'On a date' }]} />
                      {recurEndType === 'count' && (<><Input type="number" min="1" max="999" aria-label="How many times" className="ui-taskform__num" value={recurCount} onChange={(e) => setRecurCount(Math.max(1, parseInt(e.target.value, 10) || 1))} /><span>times</span></>)}
                      {recurEndType === 'until' && (
                        <DateInput aria-label="Repeat until" className="ui-taskform__date" value={toDateStr(recurUntil)} min={toDateStr(today)} onChange={(e) => setRecurUntil(fromDateStr(e.target.value))} />
                      )}
                    </div>
                  </div>
                )}
                <Checkbox checked={busy} onChange={setBusy} label="Block this time on my calendar" />
              </>
            )}
          </div>
        </article>

        {!isLeaveMode && (
          <article className="ui-taskform__card" aria-labelledby="task-where-label">
            <header className="ui-taskform__cardhead">
              <span id="task-where-label" className="ui-taskform__eyebrow">Where</span>
              <SegmentedControl label="Where" value={whereMode} onChange={chooseWhere}
                options={[{ value: 'place', label: 'In person' }, { value: 'video', label: 'Video' }, { value: 'none', label: 'None' }]} />
            </header>
            {whereMode === 'place' && (
              <div className="ui-taskform__cardbody">
                <LocationInput id="location-input" value={location || ''} onChange={(val) => setLocation(val)} placeholder="Search an address or venue" />
                <Toggle checked={showTravelTime} onChange={setShowTravelTime} label="Add travel time" />
                {showTravelTime && (
                  <div className="ui-taskform__travel">
                    <ChipGroup label="Travelling from" scroll={false} value={travelFrom} onChange={setTravelFrom}
                      options={[{ value: 'previous', label: 'Last place' }, { value: 'home', label: 'Home' }, { value: 'office', label: 'Office' }, { value: 'custom', label: 'Somewhere else' }]} />
                    {travelFrom === 'custom' && <Input aria-label="Starting address" placeholder="Starting address" value={customTravelAddress} onChange={(e) => setCustomTravelAddress(e.target.value)} />}
                    {travelInfoLoading && <p className="ui-taskform__hint">Working out the travel time…</p>}
                    {!travelInfoLoading && travelInfo && (() => {
                      const tp = currentUser?.preferences?.travel || {};
                      const originAddr = travelFrom === 'home' ? tp.homeAddress : travelFrom === 'office' ? tp.workplaceAddress : customTravelAddress;
                      const mapsUrl = originAddr && location ? `https://www.google.com/maps/dir/${encodeURIComponent(originAddr)}/${encodeURIComponent(location)}` : null;
                      return (
                        <p className="ui-taskform__hint">
                          {MODE_EMOJI[travelInfo.recommendedMode] || '🚗'} About {travelInfo.travelMins} min, {travelInfo.bufferMins} min with a buffer
                          {travelInfo.distanceText && ` · ${travelInfo.distanceText}`}
                          {mapsUrl && <> · <a href={mapsUrl} target="_blank" rel="noreferrer">Open in Maps</a></>}
                          {travelInfo.needsHotel && <><br />Over 150 km: think about somewhere to stay.</>}
                        </p>
                      );
                    })()}
                    {!travelInfoLoading && !travelInfo && travelFrom && travelFrom !== 'custom' && location && (() => {
                      const tp = currentUser?.preferences?.travel || {};
                      const addr = travelFrom === 'home' ? tp.homeAddress : tp.workplaceAddress;
                      if (!addr) return <p className="ui-taskform__hint">Save your {travelFrom} address in Settings, Travel, to work this out.</p>;
                      return <p className="ui-taskform__hint">About {tp.travelBuffer || 60} min travel buffer (from your travel settings).</p>;
                    })()}
                  </div>
                )}
              </div>
            )}
            {whereMode === 'video' && (
              <div className="ui-taskform__cardbody">
                <ChipGroup label="Video service" scroll={false} value={meetingType} onChange={setMeetingType}
                  options={VIDEO.map((svc) => ({ value: svc, label: svc, icon: svc === 'Google Meet' ? '🎥' : svc === 'Zoom' ? '📹' : '💼' }))} />
                {learntMeetingType && meetingType === learntMeetingType && !(defaultMeetingType && !["Custom Link", "Address"].includes(defaultMeetingType)) && (
                  <p className="ui-taskform__hint">
                    {learntPlatform.mode === 'auto' ? `${learntPlatform.label}, your usual platform, chosen by Aidey.` : `Aidey's suggestion: ${learntPlatform.label}, your usual platform.`}
                  </p>
                )}
                <div className="ui-taskform__row">
                  <Button variant="secondary" size="sm" onClick={generate} disabled={generating || !connected}>
                    {generating ? 'Creating…' : `Create a ${meetingType || 'meeting'} link`}
                  </Button>
                  <span className="ui-taskform__hint">{connected ? `${meetingType} is connected.` : `Connect ${meetingType} in Settings, Video meetings, or paste a link below.`}</span>
                </div>
                <Input aria-label="Meeting link" placeholder="Or paste a meeting link" value={location || ''} onChange={(e) => setLocation(e.target.value)} />
              </div>
            )}
            {whereMode === 'none' && <p className="ui-taskform__none">No location needed.</p>}
          </article>
        )}

        <article className="ui-taskform__card">
          <button type="button" className="ui-taskform__cardhead ui-taskform__more" aria-expanded={more} aria-controls="task-more" onClick={() => setMore((m) => !m)}>
            <span className="ui-taskform__eyebrow">{isLeaveMode ? 'Note for your manager' : 'Notes, people & files'}</span>
            <span className="ui-taskform__summary">{moreSummary}</span>
            <span className="ui-taskform__chev" aria-hidden="true">{more ? '−' : '+'}</span>
          </button>
          <div id="task-more" className="ui-taskform__cardbody" hidden={!more}>
            <div className="ui-taskform__mention">
              <Textarea id="task-details" rows={3} aria-label={isLeaveMode ? 'Note for your manager' : 'Notes'}
                placeholder={isLeaveMode ? 'Add a note for your manager…' : 'Add notes… use @name to mention someone'}
                value={detailsWithMentions} onChange={handleDescriptionChange} onKeyDown={handleMentionKeyDown} />
              {showMentionDropdown && mentionSuggestions.length > 0 && (
                <div className="ui-taskform__mentions" role="listbox" aria-label="Mention someone">
                  {mentionSuggestions.map((u) => (
                    <button key={u.id} type="button" role="option" aria-selected="false" className="ui-assign__option" onClick={() => handleMentionSelect(u)}>
                      <Avatar name={u.name} size={24} color="pastel-lilac" />
                      <span className="ui-assign__name">{u.name}</span>
                      {u.email && <span className="ui-assign__sub">{u.email}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!isLeaveMode && !isPersonalWorkspace && (
              <div className="ui-taskform__sub">
                <span className="ui-taskform__label" id="task-invite-label">Invite</span>
                <div className="ui-assign__box">
                  {invitedEmails.map((em) => (
                    <span key={em} className="ui-assign__chip">✉️ {em}
                      <button type="button" className="ui-assign__remove ui-hit" aria-label={`Remove ${em}`} onClick={() => writeTeam(assignedIds, invitedEmails.filter((x) => x !== em))}>×</button>
                    </span>
                  ))}
                  <input className="ui-assign__input" type="email" aria-labelledby="task-invite-label" placeholder="Email, then Enter"
                    value={inviteText} onChange={(e) => { setInviteText(e.target.value); setInviteError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addInvite(); } }} onBlur={addInvite} />
                </div>
                {inviteError && <p className="ui-field__error">{inviteError}</p>}
              </div>
            )}

            {!isLeaveMode && (
              <div className="ui-taskform__sub">
                <span className="ui-taskform__label">Files & links</span>
                {(editingId ? taskAttachments : []).map((a) => (
                  <div key={`${a.stored ? 's' : 'a'}${a.id}`} className="ui-taskform__file">
                    <span className="ui-taskform__filename">📎 {a.fileName}</span>
                    {a.stored || a.storageKey
                      ? <Button variant="tertiary" size="sm" onClick={() => openAttachment(a)}>Download</Button>
                      : <a href={a.fileUrl || a.externalUrl} target="_blank" rel="noopener noreferrer">Open</a>}
                    <button type="button" className="ui-assign__remove ui-hit" aria-label={`Remove ${a.fileName}`} onClick={() => handleDeleteAttachment(a)}>×</button>
                  </div>
                ))}
                {editingId && [...attachmentUploading].map((name) => <div key={name} className="ui-taskform__file"><span className="ui-taskform__filename">{name}</span><span className="ui-taskform__hint">Uploading…</span></div>)}
                {!editingId && pendingFiles.map((f, i) => (
                  <div key={`f${i}`} className="ui-taskform__file">
                    <span className="ui-taskform__filename">📎 {f.name}</span>
                    <button type="button" className="ui-assign__remove ui-hit" aria-label={`Remove ${f.name}`} onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                {!editingId && pendingLinks.map((link, i) => (
                  <div key={`l${i}`} className="ui-taskform__file">
                    <a className="ui-taskform__filename" href={link} target="_blank" rel="noopener noreferrer">🔗 {link}</a>
                    <button type="button" className="ui-assign__remove ui-hit" aria-label={`Remove ${link}`} onClick={() => setPendingLinks((prev) => prev.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                <div className="ui-taskform__row">
                  <label className="ui-btn ui-btn--secondary ui-btn--sm ui-taskform__attach">
                    + Attach file
                    <input ref={pendingFileInputRef} type="file" multiple className="ui-taskform__fileinput"
                      onChange={editingId ? handleAttachFiles : (e) => { const files = Array.from(e.target.files || []); if (files.length) setPendingFiles((prev) => [...prev, ...files]); e.target.value = ''; }} />
                  </label>
                  {!editingId && (
                    <>
                      <Input type="url" aria-label="Paste a link" placeholder="Paste a link" className="ui-taskform__link" value={linkInput}
                        onChange={(e) => setLinkInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPendingLink(); } }} />
                      <Button variant="secondary" size="sm" onClick={handleAddPendingLink} disabled={!linkInput.trim()}>Add link</Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </article>
      </form>

      {showLogTime && (
        <Suspense fallback={null}>
          <LogTimeModal task={{ id: editingId, title, projectId: project?.value ?? null }} onClose={() => setShowLogTime(false)} onSaved={() => setShowLogTime(false)} />
        </Suspense>
      )}
    </Modal>
  );
}

export default TaskForm;
